import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { dhfApi } from '../../utils/dhfApi';
import {
  Chip, coverageChip, doneChip, healthColor, isEmptyHtml, safeHtml, testsChip, utc,
} from './dhfUi';
import { Diff, ReasonChip, downloadVariant, hasChange } from './variantsUi';
import '../../styles/DocReview.css';
import '../../styles/Dhf.css';
import '../../styles/DhfVariants.css';

const STATE_CHIP = {
  changed: { tone: 'blue', text: 'Changed' },
  added: { tone: 'green', text: 'New' },
  removed: { tone: 'grey', text: 'Removed' },
};

/** Rich text the person edits in place; read back as sanitised HTML on save. */
function RichEdit({ html, editRef, label }) {
  return (
    <div ref={editRef} className="dhfv-rich" contentEditable suppressContentEditableWarning role="textbox"
         aria-multiline="true" aria-label={label}
         dangerouslySetInnerHTML={{ __html: safeHtml(html) }} />
  );
}

const sanitize = (h) => DOMPurify.sanitize(h || '', { USE_PROFILES: { html: true } });

/** Paragraph numbers over the live (not removed) paragraphs, as the branch's export prints them. */
function numberTree(nodes, prefix = '', out = {}) {
  let n = 0;
  (nodes || []).forEach((x) => {
    if (x.kind === 'par' && x.state !== 'removed') {
      n += 1;
      out[x.nid] = `${prefix}${n}`;
      numberTree(x.children, `${prefix}${n}.`, out);
    } else {
      numberTree(x.children, prefix, out);
    }
  });
  return out;
}

function differs(n) {
  return n.state !== 'same' || n.conflict || (n.children || []).some(differs);
}

/**
 * One branch, as a document you can edit (mockup screen 2). Every change stays in this app;
 * the chips on the right are the ORIGIN item's status, traces and tests, read by the DHF build
 * (D3). A node from the parent is Removed (kept, hidden), a node made here can be Deleted.
 */
export default function DhfVariantPage() {
  const { setupId, vid } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [showRemoved, setShowRemoved] = useState(true);
  const [showChanges, setShowChanges] = useState(true);
  const [showChips, setShowChips] = useState(true);
  const [editing, setEditing] = useState(null);          // {nid, name, level_i}
  const [adding, setAdding] = useState(null);            // {parent_nid, kind, name, level_i}
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [located, setLocated] = useState(null);
  const descRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setData(await dhfApi.variant(setupId, vid));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [setupId, vid]);
  useEffect(() => { load(); }, [load]);

  const nums = useMemo(() => numberTree(data?.tree), [data]);
  const levels = data?.item_levels || [];

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = () => run(async () => {
    const body = { name: editing.name };
    if (descRef.current) body.description = sanitize(descRef.current.innerHTML);
    if (editing.level_i !== undefined && editing.level_i !== null) body.level_i = Number(editing.level_i);
    await dhfApi.editNode(setupId, vid, editing.nid, body);
    setEditing(null);
  });

  const saveAdd = () => run(async () => {
    const body = { parent_nid: adding.parent_nid, kind: adding.kind, name: adding.name,
                   description: descRef.current ? sanitize(descRef.current.innerHTML) : '' };
    if (adding.kind === 'item' && adding.level_i !== '') body.level_i = Number(adding.level_i);
    await dhfApi.addNode(setupId, vid, body);
    setAdding(null);
  });

  const startAdd = (parentNid, kind) => {
    setEditing(null);
    setAdding({ parent_nid: parentNid, kind, name: '', level_i: levels[0]?.level_i ?? '' });
  };

  const locate = (nid) => {
    setLocated(nid);
    const el = document.getElementById(`dhfv-n-${nid}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const rowActions = (n) => (
    <div className="dhfv-row-actions">
      {n.state !== 'removed' && (
        <button type="button" className="dhfv-act" disabled={busy}
                onClick={() => { setAdding(null); setEditing({ nid: n.nid, name: n.name, level_i: n.origin_id ? null : (n.level_i ?? levels[0]?.level_i ?? null) }); }}>
          Edit
        </button>
      )}
      <button type="button" className="dhfv-act" aria-label={`Move ${n.name} up`} disabled={busy}
              onClick={() => run(() => dhfApi.moveNode(setupId, vid, n.nid, 'up'))}>↑</button>
      <button type="button" className="dhfv-act" aria-label={`Move ${n.name} down`} disabled={busy}
              onClick={() => run(() => dhfApi.moveNode(setupId, vid, n.nid, 'down'))}>↓</button>
      {n.state === 'added' ? (
        <button type="button" className="dhfv-act dhfv-act--bad" disabled={busy}
                onClick={() => { if (window.confirm(`Delete "${n.name}" from this branch?`)) run(() => dhfApi.deleteNode(setupId, vid, n.nid)); }}>
          Delete
        </button>
      ) : n.state === 'removed' ? (
        <button type="button" className="dhfv-act" disabled={busy}
                onClick={() => run(() => dhfApi.editNode(setupId, vid, n.nid, { removed: false }))}>Restore</button>
      ) : (
        <button type="button" className="dhfv-act" disabled={busy} title="Hide it in this branch and its exports. The origin keeps it."
                onClick={() => run(() => dhfApi.editNode(setupId, vid, n.nid, { removed: true }))}>Remove</button>
      )}
    </div>
  );

  const editForm = (n, isPar) => (
    <div className="dhfv-edit">
      <label className="dhfv-tiny" htmlFor={`dhfv-name-${n.nid}`}>{isPar ? 'Heading' : 'Name'}</label>
      <input id={`dhfv-name-${n.nid}`} className="dhfv-edit-name" value={editing.name} autoFocus
             onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
      <span className="dhfv-tiny">Description</span>
      <RichEdit html={n.description} editRef={descRef} label="Description" />
      {!isPar && editing.level_i !== null && editing.level_i !== undefined && (
        <label className="dhfv-tiny">Level (what a merge creates it as)
          <select value={editing.level_i} onChange={(e) => setEditing({ ...editing, level_i: e.target.value })}>
            {levels.map((l) => <option key={l.level_i} value={l.level_i}>{l.name}{l.label ? ` — ${l.label}` : ''}</option>)}
          </select>
        </label>
      )}
      <div className="dhfv-edit-f">
        <button type="button" className="drv-btn drv-btn--primary" disabled={busy || !editing.name.trim()} onClick={saveEdit}>Save</button>
        <button type="button" className="drv-btn" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
      </div>
    </div>
  );

  const addForm = () => (
    <div className="dhfv-edit dhfv-edit--new">
      <span className="dhfv-new-tag">{adding.kind === 'par' ? 'NEW PARAGRAPH' : 'NEW ITEM'}</span>
      <label className="dhfv-tiny" htmlFor="dhfv-new-name">{adding.kind === 'par' ? 'Heading' : 'Name'}</label>
      <input id="dhfv-new-name" className="dhfv-edit-name" value={adding.name} autoFocus
             onChange={(e) => setAdding({ ...adding, name: e.target.value })} />
      <span className="dhfv-tiny">Description</span>
      <RichEdit html="" editRef={descRef} label="Description" />
      {adding.kind === 'item' && levels.length > 0 && (
        <label className="dhfv-tiny">Level (what a merge creates it as)
          <select value={adding.level_i} onChange={(e) => setAdding({ ...adding, level_i: e.target.value })}>
            {levels.map((l) => <option key={l.level_i} value={l.level_i}>{l.name}{l.label ? ` — ${l.label}` : ''}</option>)}
          </select>
        </label>
      )}
      <div className="dhfv-edit-f">
        <button type="button" className="drv-btn drv-btn--primary" disabled={busy || !adding.name.trim()} onClick={saveAdd}>Add</button>
        <button type="button" className="drv-btn" disabled={busy} onClick={() => setAdding(null)}>Cancel</button>
      </div>
    </div>
  );

  const stateChip = (n) => {
    if (n.conflict) {
      return <span className="dhfv-state dhfv-state--warn" title="The parent changed it too — resolve it in Pull">Conflict</span>;
    }
    const s = STATE_CHIP[n.state];
    return s ? <span className={`dhfv-state dhfv-state--${s.tone}`}>{s.text}</span> : null;
  };

  const desc = (n) => {
    if (showChanges && n.state === 'changed' && hasChange(n.diff?.description)) {
      return <div className="dhf-item-desc"><Diff segs={n.diff.description} /></div>;
    }
    return !isEmptyHtml(n.description) && <div className="dhf-item-desc" dangerouslySetInnerHTML={{ __html: safeHtml(n.description) }} />;
  };

  const title = (n) => (showChanges && n.state === 'changed' && hasChange(n.diff?.name)
    ? <Diff segs={n.diff.name} /> : n.name);

  const renderNodes = (nodes, depth) => (nodes || []).map((n) => {
    if (!showRemoved && n.state === 'removed') return null;
    if (onlyDiff && !differs(n)) return null;
    const removed = n.state === 'removed';
    const loc = located === n.nid ? ' dhfv-located' : '';
    if (n.kind === 'par') {
      const H = `h${Math.min(depth + 1, 4)}`;
      return (
        <section key={n.nid}>
          <div id={`dhfv-n-${n.nid}`} className={`dhfv-par-row${removed ? ' dhfv-removed' : ''}${loc}`}
               onClick={() => { if (located === n.nid) setLocated(null); }}>
            {editing?.nid === n.nid ? editForm(n, true) : (
              <>
                <H className={`dhf-par dhf-par--${Math.min(depth, 3)}`}>
                  {nums[n.nid] && <span className="dhf-par-num">{nums[n.nid]}</span>}{title(n)}
                </H>
                {stateChip(n)}
                {rowActions(n)}
              </>
            )}
          </div>
          {editing?.nid !== n.nid && !removed && desc(n)}
          {removed && <p className="dhfv-tiny">Hidden in this branch and its exports. The origin is not touched.</p>}
          {renderNodes(n.children, depth + 1)}
          {!removed && (adding?.parent_nid === n.nid ? addForm() : (
            <div className="dhfv-add-row">
              <button type="button" className="dhfv-add" onClick={() => startAdd(n.nid, 'item')}>＋ Add item to {nums[n.nid] || n.name}</button>
              <button type="button" className="dhfv-add" onClick={() => startAdd(n.nid, 'par')}>＋ Sub-paragraph</button>
            </div>
          ))}
        </section>
      );
    }
    const f = n.fact;
    return (
      <div key={n.nid}>
        <div id={`dhfv-n-${n.nid}`} className={`dhf-item dhfv-item dhfv-item--${n.conflict ? 'conflict' : n.state}${loc}`}
             onClick={() => { if (located === n.nid) setLocated(null); }}>
          {editing?.nid === n.nid ? editForm(n, false) : (
            <>
              <div className="dhf-item-main">
                {n.url ? <a className="dhf-key" href={n.url} target="_blank" rel="noopener noreferrer">{n.key} ↗</a>
                  : n.key ? <span className="dhf-key">{n.key}</span> : null}
                <span className="dhf-item-name">{title(n)}</span>
                {!removed && desc(n)}
                {n.conflict && <div className="dhfv-tiny dhfv-warn-t">The parent changed this item too — <Link to={`/dhf/${setupId}/variant/${vid}/pull`}>resolve it in Pull</Link>.</div>}
                {rowActions(n)}
              </div>
              {showChips && (
                <div className="dhf-item-side">
                  {stateChip(n)}
                  {f ? (<>{doneChip(f, f.status)}{coverageChip(f)}{testsChip(f.tests)}</>)
                    : n.state === 'added' && n.kind === 'item' ? <Chip tone="warn">Not traced until merged</Chip>
                      : n.origin_id ? <Chip tone="neutral">not in panel</Chip> : null}
                  {n.state === 'changed' && f?.tests && (f.tests.pass + f.tests.fail + f.tests.notrun) > 0 && (
                    <span className="dhfv-tiny">tests check the origin text</span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {renderNodes(n.children, depth)}
      </div>
    );
  });

  const outline = (nodes, depth = 1) => (nodes || []).filter((n) => n.kind === 'par').flatMap((n) => {
    const changed = [];
    const walk = (xs) => (xs || []).forEach((x) => { if (x.kind !== 'par') { if (x.state !== 'same' || x.conflict) changed.push(x); walk(x.children); } });
    walk(n.children);
    const conflicts = changed.filter((x) => x.conflict).length;
    const added = changed.filter((x) => x.state === 'added').length;
    const edited = changed.filter((x) => x.state === 'changed').length;
    return [
      <li key={n.nid} style={{ paddingLeft: (depth - 1) * 12 }} className={n.state === 'removed' ? 'dhfv-ol-removed' : ''}>
        <a href={`#dhfv-n-${n.nid}`} onClick={(e) => { e.preventDefault(); locate(n.nid); }}>
          <span>{nums[n.nid] || ''} {n.name}</span>
          {n.state === 'added' ? <span className="dhfv-state dhfv-state--green">new</span>
            : n.state === 'removed' ? <span className="dhfv-state dhfv-state--grey">removed</span>
              : conflicts ? <span className="dhfv-state dhfv-state--warn">conflict</span>
                : (edited || added) ? <span className="dhfv-state dhfv-state--blue">{[edited && `${edited} changed`, added && `＋${added}`].filter(Boolean).join(' · ')}</span> : null}
        </a>
      </li>,
      ...outline(n.children, depth + 1),
    ];
  });

  if (error && !data) return <div className="drv-page"><div className="drv-error" role="alert">{error}</div></div>;
  if (!data) return <div className="drv-page"><div className="drv-muted">Loading…</div></div>;
  const h = data.score?.health;
  const base = `/dhf/${setupId}/variant/${vid}`;

  return (
    <div className="drv-page">
      <div className="dhf-doc-bar">
        <div className="dhf-doc-bar-l">
          <Link to={`/dhf/${setupId}/doc/${data.doc_id}/variants`}>← Branches</Link>
          {renaming ? (
            <form className="dhfv-rename" onSubmit={(e) => { e.preventDefault(); run(async () => { await dhfApi.updateVariant(setupId, vid, renaming); setRenaming(null); }); }}>
              <input aria-label="Branch name" value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} />
              <input aria-label="Reason" value={renaming.reason} onChange={(e) => setRenaming({ ...renaming, reason: e.target.value })} />
              <button type="submit" className="drv-btn drv-btn--primary" disabled={!renaming.name.trim()}>Save</button>
              <button type="button" className="drv-btn" onClick={() => setRenaming(null)}>Cancel</button>
            </form>
          ) : (
            <>
              <strong>{data.name}</strong>
              <ReasonChip reason={data.reason} />
              <button type="button" className="dhfv-act" onClick={() => setRenaming({ name: data.name, reason: data.reason })}>Rename</button>
            </>
          )}
          <span className="drv-muted">branch of {data.doc.url ? <a className="dhf-key" href={data.doc.url} target="_blank" rel="noopener noreferrer">{data.doc.key} ↗</a> : data.doc.key}
            {data.parent_id ? <> · from <strong>{data.parent_label}</strong></> : null}</span>
          <span className="dhf-chip" style={{ background: '#F4F1FB', color: healthColor(h) }}>health {h ?? '—'}</span>
        </div>
        <div className="dhf-doc-bar-r">
          <div className="dhfv-seg" role="group" aria-label="Show">
            <button type="button" aria-pressed={!onlyDiff} className={!onlyDiff ? 'dhfv-seg--on' : ''} onClick={() => setOnlyDiff(false)}>All</button>
            <button type="button" aria-pressed={onlyDiff} className={onlyDiff ? 'dhfv-seg--on' : ''} onClick={() => setOnlyDiff(true)}>Only differences</button>
          </div>
          <label><input type="checkbox" checked={showChanges} onChange={(e) => setShowChanges(e.target.checked)} /> Mark changes</label>
          <label><input type="checkbox" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)} /> Show removed</label>
          <label><input type="checkbox" checked={showChips} onChange={(e) => setShowChips(e.target.checked)} /> Status chips</label>
          {data.behind ? <Link className="drv-btn dhfv-btn-warn" to={`${base}/pull`}>{data.parent_id ? 'Parent' : 'Origin'} changed · {data.behind}</Link>
            : <Link className="drv-btn" to={`${base}/pull`}>Pull</Link>}
          <Link className="drv-btn" to={`/dhf/${setupId}/doc/${data.doc_id}/variants/compare?a=${data.parent_id || 'origin'}&b=${vid}`}>Compare</Link>
          <Link className="drv-btn drv-btn--primary" to={`${base}/merge`}>Merge to Orcanos…</Link>
          <button type="button" className="drv-btn" onClick={() => downloadVariant(setupId, vid, 'docx').catch((e) => setError(e.message))}>Export Word</button>
          <button type="button" className="drv-btn" onClick={() => downloadVariant(setupId, vid, 'pdf').catch((e) => setError(e.message))}>PDF</button>
        </div>
      </div>

      {error && <div className="drv-error" role="alert">{error}</div>}

      <div className="dhf-doc-layout">
        <aside className="dhf-aside dhfv-aside">
          <div className="drv-card dhfv-health">
            <strong>Branch health · {h ?? '—'}</strong>
            {(data.score?.parts || []).filter((p) => p.available).map((p) => (
              <div key={p.key} className="dhfv-health-row"><span>{p.label}</span><b>{Math.round(100 - (100 * (p.share || 0)))}</b></div>
            ))}
            <p className="dhfv-tiny">The DHF score over this branch's items. Traces and test results are the origin items'.
              {data.counts.added_untraced ? <> <strong>{data.counts.added_untraced} new item{data.counts.added_untraced > 1 ? 's' : ''}</strong> count as not traced until merged.</> : null}</p>
          </div>
          <div className="drv-card dhfv-outline">
            <div className="dhfv-outline-h">Outline</div>
            <ul className="dhf-outline-list">{outline(data.tree)}</ul>
          </div>
          <div className="dhf-note dhfv-legend">
            <div><span className="dhfv-ins">text</span> added in this branch</div>
            <div><span className="dhfv-del">text</span> removed in this branch</div>
            <div>Chips on the right are the <strong>origin item's</strong> status, traces and test result.</div>
          </div>
          {data.events?.length > 0 && (
            <details className="drv-card dhfv-history">
              <summary>History</summary>
              <ul>{data.events.map((e, i) => (
                <li key={i}><b>{e.action.replace('merge_', 'merge ')}</b>{e.result && e.result !== 'ok' ? ` · ${e.result}` : ''} — {e.user_name} · {utc(e.created_at)}</li>
              ))}</ul>
            </details>
          )}
        </aside>

        <article className="dhf-paper">
          <div className="dhf-paper-eyebrow">{data.doc.key} · branch {data.name} · base: {data.parent_label} as of {utc(data.base_at)}</div>
          <h1>{data.doc.name}</h1>
          <p className="drv-muted">{data.score?.n_items} items in {data.score?.n_paragraphs} paragraphs
            {' · '}{data.counts.changed} changed · {data.counts.added} new · {data.counts.removed} removed</p>
          {renderNodes(data.tree, 1)}
          {adding?.parent_nid === '' ? addForm() : (
            <div className="dhfv-add-row">
              <button type="button" className="dhfv-add" onClick={() => startAdd('', 'par')}>＋ Add paragraph</button>
              <button type="button" className="dhfv-add" onClick={() => startAdd('', 'item')}>＋ Add item at the top level</button>
            </div>
          )}
        </article>
      </div>
    </div>
  );
}
